import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Map as MLMap, Marker, NavigationControl, ScaleControl, LngLatBounds } from "maplibre-gl";
// MapLibre 6 loads its tile worker from a separate file; let Vite bundle it and tell MapLibre where it is.
import { setWorkerUrl } from "maplibre-gl";
import maplibreWorkerUrl from "maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url";
setWorkerUrl(maplibreWorkerUrl);
import type { Session } from "@supabase/supabase-js";
import { supabase, type Device, type DeviceState } from "../lib/supabase";
import { ago, fijiTime, kmh, liveSpeed, statusOf, STATUS_LABEL, type Status } from "../lib/format";
import HistoryPanel from "./HistoryPanel";
import ShareDialog from "./ShareDialog";
import { Link } from "react-router-dom";
import { useStaffRole } from "../lib/admin";
import { ALL_BASEMAPS, BASEMAP_CREDIT, MAP_STYLE, applyBasemap, saveBasemap, savedBasemap, type Basemap } from "../lib/basemap";
import BasemapPicker, { useBasemaps } from "../ui/BasemapPicker";
import { iconSvg } from "../lib/icons";
import { MK_OFFSET, markerParts, setSilhouette } from "../lib/vehicleMarker";
import { canManage, managedOrgs, type Zone } from "../lib/org";
import EventsPanel, { useAlertEvents } from "./map/EventsPanel";
import AlertsDrawer from "./map/AlertsDrawer";
import AlertsManager from "./map/AlertsManager";
import { AddressCard, MapToolbar, RulerTool, useAddressPicker, type Spot, type Tool } from "./map/MapTools";
import FindTool from "./map/FindTool";
import { PlaceForm, PlacesTool, usePlaces, usePlacesLayer, type Place, type PlaceDraft, type PlaceOrg } from "./map/Places";
import { quietNow, toggleHidden, useUserPrefs } from "../lib/prefs";
import ZoneTool from "./map/ZoneTool";
import DeviceDisplayDrawer from "./map/DeviceDisplayDrawer";
import { useTails } from "./map/useTails";
import { zoneFeature, zonesAt, zoneStack } from "../lib/geo";
import { onStyleReady } from "../lib/mapready";
import { useLiveFeed } from "../lib/liveFeed";
import FollowWindow from "./map/FollowWindow";
import QuickMenu, { type QuickAction } from "./map/QuickMenu";
import PanelArt from "./map/PanelArt";
import FleetGlance from "./map/FleetGlance";
import AppFooter from "../ui/AppFooter";
import CompanyBadge from "../ui/CompanyBadge";
import TeamPresence from "../ui/TeamPresence";
import AdminLink from "../ui/AdminLink";
import { QUALITY_NOTE, type Quality } from "../../netlify/lib/gpsquality";
import type { GeoJSONSource } from "maplibre-gl";
import { stoppedLabel, useStoppedFor } from "../lib/stopped";
import UserMenu from "../ui/UserMenu";

const FIJI_CENTER: [number, number] = [177.45, -17.75];
// V3.10.1: last zones seen by this signed-in user, so coming back to the map from another page shows them at once
let zoneCache: { uid: string; zones: Zone[] } | null = null;
const ZONE_RETRY_MS = [2000, 5000, 15000];

export default function MapPage({ session }: { session: Session }) {
  const staffRole = useStaffRole(session.user.id);
  const [devices, setDevices] = useState<Device[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [now, setNow] = useState(Date.now());
  const [loadError, setLoadError] = useState<string | null>(null);
  const [tab, setTab] = useState<"objects" | "events" | "history">("objects");
  const [histPreset, setHistPreset] = useState<{ kind: "24h" | "today" | "yesterday"; n: number } | null>(null);
  const [follows, setFollows] = useState<string[]>([]);
  const [alertsFor, setAlertsFor] = useState<string | null>(null);
  const [alertsMgr, setAlertsMgr] = useState(false);
  const [sharingId, setSharingId] = useState<string | null>(null);
  const [managed, setManaged] = useState<Set<string> | "all" | null>(null);
  const [events, setEvents] = useAlertEvents();
  const [zones, setZones] = useState<Zone[]>(() => (zoneCache?.uid === session.user.id ? zoneCache.zones : []));
  // V3.10.1: zones sometimes stayed hidden after coming back to the map from another page until the browser tab was
  // switched. A load that fails, or comes back empty while we know this user has zones, is now retried (2 s, 5 s, 15 s)
  // instead of being left until the next tab switch. `force` (after the zone tool saves or deletes) accepts any answer.
  const zoneRetry = useRef<ReturnType<typeof setTimeout> | null>(null);
  const loadZones = useCallback(async (opts?: { force?: boolean; attempt?: number }) => {
    const attempt = opts?.attempt ?? 0;
    if (zoneRetry.current) { clearTimeout(zoneRetry.current); zoneRetry.current = null; }
    const { data, error } = await supabase.from("geofences").select("*").order("name");
    const known = zoneCache?.uid === session.user.id ? zoneCache.zones.length : 0;
    const doubtful = !error && !!data && data.length === 0 && known > 0 && !opts?.force;
    if ((error || !data || doubtful) && attempt < ZONE_RETRY_MS.length) {
      zoneRetry.current = setTimeout(() => loadZones({ attempt: attempt + 1 }), ZONE_RETRY_MS[attempt]);
      return;   // keep what we have meanwhile (never blank the map on a failed load)
    }
    if (error || !data) return;
    zoneCache = { uid: session.user.id, zones: data as Zone[] };
    setZones(data as Zone[]);
  }, [session.user.id]);
  useEffect(() => () => { if (zoneRetry.current) clearTimeout(zoneRetry.current); }, []);
  // V3.9.0: refresh zones when coming back to the tab/window
  useEffect(() => {
    const again = () => { if (document.visibilityState === "visible") loadZones(); };
    document.addEventListener("visibilitychange", again);
    return () => document.removeEventListener("visibilitychange", again);
  }, [loadZones]);
  useEffect(() => {
    managedOrgs(session.user.id).then(setManaged);
    loadZones();
  }, [session.user.id, alertsFor, loadZones]);
  // V3.3.0 map tools
  const [tool, setTool] = useState<Tool>(null);
  const [spot, setSpot] = useState<Spot | null>(null);
  // V3.9.0: hidden devices (per account), alert snooze, saved places
  const prefs = useUserPrefs();
  const hiddenSet = useMemo(() => new Set(prefs.hidden_devices), [prefs.hidden_devices]);
  const quiet = quietNow(prefs, now);
  const placesState = usePlaces();
  const [placeDraft, setPlaceDraft] = useState<PlaceDraft | null>(null);
  const [placeOrgs, setPlaceOrgs] = useState<PlaceOrg[]>([]);
  const [myRoles, setMyRoles] = useState<Record<string, string>>({});
  useEffect(() => {
    (async () => {
      const [{ data: mem }, { data: sup }] = await Promise.all([
        supabase.from("memberships").select("org_id, role, organisations(name)").eq("user_id", session.user.id),
        supabase.rpc("my_support_session"),
      ]);
      const roles: Record<string, string> = {};
      const orgs: PlaceOrg[] = [];
      for (const m of (mem ?? []) as any[]) { roles[m.org_id] = m.role; if (m.role !== "viewer") orgs.push({ id: m.org_id, name: m.organisations?.name ?? "My company" }); }
      if (sup?.org_id) orgs.push({ id: sup.org_id, name: sup.org_name ?? "Support company" });
      setMyRoles(roles); setPlaceOrgs(orgs);
    })();
  }, [session.user.id]);
  const canEditPlace = (pl: Place) => !!staffRole || myRoles[pl.org_id] === "org_owner" || (pl.created_by === session.user.id && myRoles[pl.org_id] !== "viewer");
  const [displayFor, setDisplayFor] = useState<string | null>(null);
  const [showZones, setShowZones] = useState<boolean>(() => { try { return localStorage.getItem("tf.zones.show") !== "0"; } catch { return true; } });
  useEffect(() => { try { localStorage.setItem("tf.zones.show", showZones ? "1" : "0"); } catch { /* storage unavailable */ } }, [showZones]);
  const newEvents = events.filter((e) => !e.ack_at).length;
  const [histDevice, setHistDevice] = useState<string | null>(null);
  const [mapObj, setMapObj] = useState<MLMap | null>(null);
  const [barEl, setBarEl] = useState<HTMLDivElement | null>(null);
  const [wrapEl, setWrapEl] = useState<HTMLElement | null>(null);
  const basemaps = useBasemaps(staffRole === undefined ? undefined : !!staffRole);
  const [basemap, setBasemap] = useState<Basemap>(savedBasemap(ALL_BASEMAPS));
  // Only correct the choice once we know which layers this user may use (avoids the picker showing
  // "Streets" while the map still shows the saved satellite layer).
  useEffect(() => { if (basemaps && !basemaps.includes(basemap)) setBasemap("streets"); }, [basemaps]); // eslint-disable-line
  // V3.2.0: collapsible side panel (remembered per browser) for a full-width map
  const [collapsed, setCollapsed] = useState<boolean>(() => { try { return localStorage.getItem("tf.panel.collapsed") === "1"; } catch { return false; } });
  useEffect(() => {
    try { localStorage.setItem("tf.panel.collapsed", collapsed ? "1" : "0"); } catch { /* storage unavailable */ }
    const id = requestAnimationFrame(() => map.current?.resize());
    return () => cancelAnimationFrame(id);
  }, [collapsed]);
  const [groups, setGroups] = useState<{ id: string; name: string }[]>([]);
  const [groupFilter, setGroupFilter] = useState("");
  useEffect(() => {
    supabase.from("device_groups").select("id, name").order("name").then(({ data }) => setGroups(data ?? []));
  }, []);

  const mapEl = useRef<HTMLDivElement>(null);
  const map = useRef<MLMap | null>(null);
  const markers = useRef(new Map<string, Marker>());
  const fitted = useRef(false);

  // ---- load devices + live state (V3.12.0: useLiveFeed keeps the subscription healthy after sleep / network drops)
  const loadDevices = useCallback(async () => {
    const { data, error } = await supabase
      .from("devices")
      .select("id, org_id, name, model, unique_id, status, icon, icon_color, tail_on, tail_minutes, tail_seconds, tail_color, group_id, device_state(*)")
      .order("name");
    if (error) return setLoadError(error.message);
    setLoadError(null);
    setDevices(
      (data ?? []).map((d: any) => ({
        ...d,
        state: Array.isArray(d.device_state) ? d.device_state[0] ?? null : d.device_state ?? null,
      })),
    );
    setNow(Date.now());
  }, []);
  useEffect(() => { loadDevices(); }, [loadDevices]);
  const onStateRow = useCallback((s: DeviceState) => {
    if (!s?.device_id) return;
    setDevices((prev) => prev.map((d) => (d.id === s.device_id ? { ...d, state: s } : d)));
  }, []);
  const feed = useLiveFeed("device_state_live", "device_state", onStateRow, loadDevices);
  const live = feed === "live";
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 15000); return () => clearInterval(t); }, []);

  // ---- map init
  useEffect(() => {
    if (!mapEl.current || map.current) return;
    const m = new MLMap({
      container: mapEl.current,
      style: MAP_STYLE,
      center: FIJI_CENTER,
      zoom: 9,
      attributionControl: false, // credits live in the footer (AppFooter)
    });
    m.addControl(new NavigationControl({ visualizePitch: false }), "top-right");
    m.addControl(new ScaleControl({ unit: "metric" }), "top-left");
    map.current = m;
    setMapObj(m);
    return () => {
      map.current?.remove();
      map.current = null;
      setMapObj(null);
    };
  }, []);

  useEffect(() => {
    const m = map.current;
    if (!m) return;
    saveBasemap(basemap);
    applyBasemap(m, basemap);
  }, [basemap]);

  // ---- markers
  useEffect(() => {
    const m = map.current;
    if (!m) return;
    const seen = new Set<string>();
    const pts: [number, number][] = [];

    for (const d of devices) {
      if (groupFilter && (groupFilter === "none" ? d.group_id : d.group_id !== groupFilter)) continue;
      if (hiddenSet.has(d.id)) continue;   // V3.9.0: toggled off in the Objects list
      const s = d.state;
      if (!s || s.lat == null || s.lng == null) continue;
      seen.add(d.id);
      pts.push([s.lng, s.lat]);
      const st = statusOf(s, now);
      let mk: Marker | undefined = markers.current.get(d.id);
      if (!mk) {
        const el = document.createElement("button");
        el.className = "mk";
        el.addEventListener("click", (e) => {
          e.stopPropagation();
          setSelected(d.id);
        });
        const created = new Marker({ element: el, anchor: "left", offset: MK_OFFSET }).setLngLat([s.lng, s.lat]).addTo(m);
        markers.current.set(d.id, created);
        mk = created;
      }
      mk.setLngLat([s.lng, s.lat]);
      const el = mk.getElement();
      el.dataset.status = st;
      el.dataset.selected = String(selected === d.id);
      el.dataset.quality = s.quality ?? "good";
      el.dataset.custom = d.icon_color ? "true" : "false";
      if (d.icon_color) el.style.setProperty("--mk-bg", d.icon_color); else el.style.removeProperty("--mk-bg");
      el.title = s.quality && s.quality !== "good" ? QUALITY_NOTE[s.quality as Quality] : "";
      // V3.9.1: rotating silhouette, updated in place (label never rotates)
      const { dot, label } = markerParts(el);
      setSilhouette(dot, d.icon, s.course, s.speed_kn == null ? null : Number(s.speed_kn) * 1.852);
      label!.textContent = st === "late" ? `${d.name} · no signal ${ago(s.seen_at, now)}` : `${d.name} · ${liveSpeed(s, st)}`;
    }
    for (const [id, mk] of markers.current) {
      if (!seen.has(id)) {
        mk.remove();
        markers.current.delete(id);
      }
    }
    if (!fitted.current && pts.length) {
      fitted.current = true;
      if (pts.length === 1) m.jumpTo({ center: pts[0], zoom: 14 });
      else {
        const b = new LngLatBounds(pts[0], pts[0]);
        pts.forEach((p) => b.extend(p));
        m.fitBounds(b, { padding: 80, maxZoom: 14, duration: 0 });
      }
    }
  }, [devices, now, selected, groupFilter, hiddenSet]);

  // ---- zones overlay
  useEffect(() => {
    const m = map.current;
    if (!m) return;
    const draw = () => {
      const data = { type: "FeatureCollection" as const, features: zones.map((z) => ({ ...zoneFeature(z), properties: { color: z.color, name: z.name, g: z.is_global ? 1 : 0 } })) };
      const vis = showZones ? "visible" : "none";
      // V3.10.1: add whatever is missing (source and each layer separately), so a half-finished add can't leave the
      // source without its layers; an error (style still busy) is retried by the self-heal below.
      try {
        const src = m.getSource("zone-all") as GeoJSONSource | undefined;
        if (src) src.setData(data); else m.addSource("zone-all", { type: "geojson", data });
        if (!m.getLayer("zone-all-fill")) m.addLayer({ id: "zone-all-fill", type: "fill", source: "zone-all", paint: { "fill-color": ["get", "color"], "fill-opacity": 0.08 } });
        if (!m.getLayer("zone-all-line")) m.addLayer({ id: "zone-all-line", type: "line", source: "zone-all", paint: { "line-color": ["get", "color"], "line-width": ["case", ["==", ["get", "g"], 1], 2.5, 1.5], "line-dasharray": [2, 1] } });
        if (!m.getLayer("zone-all-label")) m.addLayer({ id: "zone-all-label", type: "symbol", source: "zone-all", layout: { "text-field": ["get", "name"], "text-size": 11 }, paint: { "text-color": "#1f2937", "text-halo-color": "#fff", "text-halo-width": 1.2 } });
        for (const id of ["zone-all-fill", "zone-all-line", "zone-all-label"]) m.setLayoutProperty(id, "visibility", vis);
      } catch (e) { console.warn("zones", e); }
    };
    onStyleReady(m, draw, "zones");
    // V3.9.0 self-heal: if the style is ever reloaded/replaced and our zone layers are gone, add them again
    const heal = () => { if (!m.getLayer("zone-all-line") || !m.getLayer("zone-all-fill") || !m.getLayer("zone-all-label")) onStyleReady(m, draw, "zones"); };
    m.on("styledata", heal);
    m.on("idle", heal);
    return () => { m.off("styledata", heal); m.off("idle", heal); };
  }, [zones, mapObj, showZones]);

  // ---- tails + address picker
  const visibleIds = useMemo(() => new Set(devices.filter((d) => !hiddenSet.has(d.id) && (!groupFilter || (groupFilter === "none" ? !d.group_id : d.group_id === groupFilter))).map((d) => d.id)), [devices, groupFilter, hiddenSet]);
  useTails(mapObj, devices, visibleIds, now, tab === "history");
  const pickSpot = useCallback((s: { lat: number; lng: number } | null) => setSpot(s), []);
  const pickPlace = useCallback((pl: Place) => setSpot({ lat: pl.lat, lng: pl.lng, title: pl.name, placeId: pl.id }), []);
  usePlacesLayer(mapObj, placesState.places, placesState.show && tab !== "history", placesState.hidden, pickPlace);
  const [listErr, setListErr] = useState<string | null>(null);
  const flipHidden = (d: Device) => { setListErr(null); toggleHidden(d.id).catch((e) => setListErr(`Couldn't save: ${e.message}`)); };
  useAddressPicker(mapObj, tool === "address", pickSpot, !!spot);
  useEffect(() => { if (tab === "history" && tool) setTool(null); }, [tab]); // eslint-disable-line
  useEffect(() => {
    if (tool !== "address") return;
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape" && !spot) setTool(null); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [tool, spot]);

  function quick(d: Device, a: QuickAction) {
    if (a.startsWith("hist_")) {
      setHistDevice(d.id);
      setHistPreset({ kind: a === "hist_24h" ? "24h" : a === "hist_today" ? "today" : "yesterday", n: Date.now() });
      setTab("history"); setCollapsed(false);
    } else if (a === "follow") {
      setFollows((f) => (f.includes(d.id) ? f.filter((x) => x !== d.id) : [...f, d.id].slice(-6)));
    } else if (a === "share") {
      setTab("objects"); focus(d); setSharingId(d.id);
    } else if (a === "alerts") setAlertsFor(d.id);
    else if (a === "address") { if (d.state?.lat != null) { setSpot({ lat: d.state.lat, lng: d.state.lng!, title: d.name }); map.current?.easeTo({ center: [d.state.lng!, d.state.lat] }); } }
    else if (a === "display") setDisplayFor(d.id);
  }

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return devices.filter((d) => (!groupFilter || (groupFilter === "none" ? !d.group_id : d.group_id === groupFilter))
      && (!q || d.name.toLowerCase().includes(q) || d.unique_id.includes(q)));
  }, [devices, query, groupFilter]);

  const sel = devices.find((d) => d.id === selected) ?? null;

  function focus(d: Device) {
    setSelected(d.id);
    const s = d.state;
    if (s?.lat != null && s?.lng != null) { const m = map.current; m?.flyTo({ center: [s.lng, s.lat], zoom: Math.max(m.getZoom(), 14), padding: { top: 40, bottom: 200, left: 40, right: 40 } }); }
  }

  function openHistory(id: string | null) {
    const fallback = id ?? histDevice ?? (devices.length === 1 ? devices[0].id : null);
    setHistDevice(fallback);
    setTab("history");
    setCollapsed(false);
  }

  const lastReport = devices.reduce<string | null>((best, d) => {
    const t = d.state?.fix_at; return t && (!best || Date.parse(t) > Date.parse(best)) ? t : best;
  }, null);
  const counts = devices.reduce(
    (acc, d) => ((acc[statusOf(d.state, now)] += 1), acc),
    { moving: 0, stopped: 0, late: 0, offline: 0, nodata: 0 } as Record<Status, number>,
  );

  return (
    <div className={`shell ${collapsed ? "panel-collapsed" : ""}`}>
      <header className="topbar">
        <img src="/logo.png" alt="Tracking Fiji" className="topbar-logo" />
        <span className="beta" title="Contrail — the new Tracking Fiji platform (beta)">Contrail Beta</span>
        <div className="topbar-spacer" />
        <CompanyBadge userId={session.user.id} />
        <TeamPresence />
        <span className={`live ${live ? "on" : ""}`}>{live ? "Live" : feed === "reconnecting" ? "Reconnecting…" : "Connecting…"}</span>
        <Link className="btn-ghost" to="/fleet">Fleet</Link>
        <Link className="btn-ghost" to="/reports">Reports</Link>
        <Link className="btn-ghost" to="/company">Company</Link>
        <AdminLink role={staffRole} />
        <UserMenu email={session.user.email} />
      </header>

      <aside className="panel" id="map-panel">
        <PanelArt />
        <div className="tabs">
          <button className={`tab ${tab === "objects" ? "active" : ""}`} onClick={() => setTab("objects")}>Objects</button>
          <button className={`tab ${tab === "events" ? "active" : ""}`} onClick={() => setTab("events")} title={quiet.quiet ? quiet.label : undefined}>Events
            {quiet.quiet ? <span className="badge snz" aria-label={quiet.label}>z</span> : newEvents > 0 && <span className="badge">{newEvents > 99 ? "99+" : newEvents}</span>}</button>
          <button className={`tab ${tab === "history" ? "active" : ""}`} onClick={() => openHistory(selected)}>History</button>
        </div>
        {tab === "history" ? (
          <HistoryPanel map={mapObj} devices={devices} deviceId={histDevice} onDeviceChange={setHistDevice} barTarget={barEl} overlayTarget={wrapEl} preset={histPreset} zones={zones} />
        ) : tab === "events" ? (
          <EventsPanel events={events} setEvents={setEvents} devices={devices} now={now} onAlerts={() => setAlertsMgr(true)}
            onFocus={(id, lat, lng) => { const d = devices.find((x) => x.id === id); if (!d) return; setSelected(id);
              if (lat != null && lng != null) map.current?.flyTo({ center: [lng, lat], zoom: Math.max(map.current.getZoom(), 14) }); }} />
        ) : (<>
        <div className="search">
          <input placeholder="Search name or ID" value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
        {groups.length > 0 && (
          <select className="group-filter" value={groupFilter} onChange={(e) => setGroupFilter(e.target.value)} aria-label="Filter by group">
            <option value="">All groups</option>
            {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
            <option value="none">No group</option>
          </select>
        )}
        <div className="summary">
          <span><i className="dot moving" />{counts.moving}</span>
          <span><i className="dot stopped" />{counts.stopped}</span>
          {counts.late > 0 && <span title="No signal"><i className="dot late" />{counts.late}</span>}
          <span><i className="dot offline" />{counts.offline + counts.nodata}</span>
          <span className="muted">{devices.length} objects</span>
        </div>
        {loadError && <div className="error pad">{loadError}</div>}
        {listErr && <div className="error pad">{listErr}</div>}
        {hiddenSet.size > 0 && devices.some((d) => hiddenSet.has(d.id)) && <div className="hidden-note">{devices.filter((d) => hiddenSet.has(d.id)).length} hidden on the map · tap an icon to show it</div>}
        <div className="objects-scroll">
        <ul className="list">
          {filtered.map((d) => {
            const st = statusOf(d.state, now);
            return (
              <li key={d.id} className={`${selected === d.id ? "sel" : ""} ${hiddenSet.has(d.id) ? "off-map" : ""}`} onClick={() => focus(d)}>
                <div className="row1">
                  <button type="button" className={`licon ${st} licon-btn ${hiddenSet.has(d.id) ? "off" : ""}`} aria-pressed={!hiddenSet.has(d.id)}
                    title={hiddenSet.has(d.id) ? `${d.name} is hidden on the map — click to show it` : `Click to hide ${d.name} on the map`}
                    aria-label={hiddenSet.has(d.id) ? `Show ${d.name} on the map` : `Hide ${d.name} on the map`}
                    onClick={(e) => { e.stopPropagation(); flipHidden(d); }} dangerouslySetInnerHTML={{ __html: iconSvg(d.icon, 15) }} />
                  <span className="name">{d.name}</span>
                  <span className="speed mono">{liveSpeed(d.state, st)}</span>
                  <i className={`dot ${st}`} title={STATUS_LABEL[st]} />
                  <QuickMenu following={follows.includes(d.id)} canEdit={canManage(managed, (d as any).org_id)} onAction={(a) => quick(d, a)} />
                </div>
                <div className="row2 mono">{d.state ? fijiTime(d.state.fix_at) : "No position yet"}</div>
              </li>
            );
          })}
          {!filtered.length && !loadError && <li className="empty muted">No objects found.</li>}
        </ul>
        <FleetGlance moving={counts.moving} stopped={counts.stopped} late={counts.late} offline={counts.offline + counts.nodata} now={now}
          openAlerts={newEvents} lastReport={lastReport} onAlerts={() => setTab("events")} onHistory={() => openHistory(selected)} onAlertRules={() => setAlertsMgr(true)} />
        </div>
        </>)}
      </aside>

      <main ref={setWrapEl} className={`mapwrap ${tab === "history" ? "history-mode" : ""}`}>
        <div ref={mapEl} className="map" />
        <button type="button" className="panel-toggle" onClick={() => setCollapsed((c) => !c)} aria-controls="map-panel" aria-expanded={!collapsed}
          title={collapsed ? "Show panel" : "Hide panel (full-width map)"} aria-label={collapsed ? "Show panel" : "Hide panel"}>
          <svg viewBox="0 0 12 12" aria-hidden="true"><path d="M8 1.5 3.5 6 8 10.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <BasemapPicker value={basemap} options={basemaps} onChange={setBasemap} setupHints={!!staffRole} />
        {tab !== "history" && <MapToolbar active={tool} onChange={(t) => { setTool(t); if (t !== "address") setSpot(null); }} />}
        {mapObj && tool === "ruler" && <RulerTool map={mapObj} onClose={() => setTool(null)} />}
        {mapObj && tool === "zones" && <ZoneTool map={mapObj} zones={zones} reload={() => loadZones({ force: true })} staff={!!staffRole} admin={staffRole === "skyward_admin"} managed={managed}
          show={showZones} setShow={setShowZones} onClose={() => setTool(null)} />}
        {mapObj && tool === "places" && !placeDraft && <PlacesTool map={mapObj} {...placesState} orgs={placeOrgs} canEdit={canEditPlace}
          onEdit={setPlaceDraft} onClose={() => setTool(null)} />}
        {mapObj && tool === "find" && <FindTool onClose={() => setTool(null)}
          onFound={(lat, lng, title) => { setSpot({ lat, lng, title }); mapObj.flyTo({ center: [lng, lat], zoom: Math.max(mapObj.getZoom(), 15) }); }} />}
        {placeDraft && <PlaceForm draft={placeDraft} orgs={placeOrgs} onClose={() => setPlaceDraft(null)}
          onSaved={async (pl) => { setPlaceDraft(null); await placesState.reload(); placesState.setShow(true); setSpot({ lat: pl.lat, lng: pl.lng, title: pl.name, placeId: pl.id }); }} />}
        {mapObj && tool === "address" && !spot && <div className="tool-hint">Click the map (or right-click anywhere) for coordinates · Esc to close</div>}
        {mapObj && spot && (() => {
          const pl = spot.placeId ? placesState.places.find((x) => x.id === spot.placeId) : undefined;
          const canAdd = placeOrgs.length > 0;
          return <AddressCard map={mapObj} spot={spot} zones={zones} onClose={() => setSpot(null)} note={pl?.notes}
            saveLabel={pl ? "Edit place" : "Save place"}
            onSave={pl ? (canEditPlace(pl) ? () => { setTool("places"); setPlaceDraft({ id: pl.id, org_id: pl.org_id, name: pl.name, lat: pl.lat, lng: pl.lng, color: pl.color, notes: pl.notes ?? "" }); } : undefined)
              : canAdd ? () => { setTool("places"); setPlaceDraft({ name: spot.title && spot.title !== "Found location" && spot.title !== "This location" ? spot.title : "", lat: spot.lat, lng: spot.lng, color: "#2563EB", notes: "" }); } : undefined} />;
        })()}
        {displayFor && (() => { const d = devices.find((x) => x.id === displayFor); return d ? (
          <DeviceDisplayDrawer device={d} onClose={() => setDisplayFor(null)}
            onSaved={(p) => { setDevices((prev) => prev.map((x) => (x.id === d.id ? { ...x, ...p } : x))); setDisplayFor(null); }} />) : null; })()}
        {tab === "objects" && sel && (
          <DetailStrip d={sel} now={now} staff={!!staffRole} zones={zones} onClose={() => { setSelected(null); setSharingId(null); }} onHistory={() => openHistory(sel.id)}
            sharing={sharingId === sel.id} setSharing={(v) => setSharingId(v ? sel.id : null)}
            menu={<QuickMenu following={follows.includes(sel.id)} canEdit={canManage(managed, (sel as any).org_id)} onAction={(a) => quick(sel, a)} />} />
        )}
        {follows.map((id, i) => { const d = devices.find((x) => x.id === id); return d ? (
          <FollowWindow key={id} device={d} index={i} basemap={basemap} now={now}
            onClose={() => setFollows((f) => f.filter((x) => x !== id))} onFocus={() => { setTab("objects"); focus(d); }} />) : null; })}
        {alertsMgr && <AlertsManager devices={devices as any} userEmail={session.user.email ?? ""} canEdit={(o) => canManage(managed, o)}
          onClose={() => { setAlertsMgr(false); loadZones(); }} />}
        {alertsFor && (() => { const d = devices.find((x) => x.id === alertsFor); return d ? (
          <AlertsDrawer device={d} userEmail={session.user.email ?? ""} canEdit={canManage(managed, (d as any).org_id)} onClose={() => setAlertsFor(null)} />) : null; })()}
        <div ref={setBarEl} className="playbar-slot" />
        <AppFooter overlay mapCredits={BASEMAP_CREDIT[basemap] ? [BASEMAP_CREDIT[basemap]!] : []} />
      </main>
    </div>
  );
}

function DetailStrip({ d, now, staff, zones, onClose, onHistory, sharing, setSharing, menu }: {
  d: Device; now: number; staff: boolean; zones: Zone[]; onClose: () => void; onHistory: () => void; sharing: boolean; setSharing: (v: boolean) => void; menu: ReactNode;
}) {
  const s = d.state;
  const st = statusOf(s, now);
  const stoppedFor = stoppedLabel(useStoppedFor(d.id, s, now), now);   // V3.9.2
  const a = (s?.attrs ?? {}) as Record<string, any>;
  const q = (s?.quality ?? "good") as Quality;
  const sat = a.sat != null ? Number(a.sat) : null;
  const signal = !s ? "—" : q === "no_fix" ? "No fix" : q === "suspect" ? "Unconfirmed" : q === "low" ? "Weak" : sat != null ? `Good (${sat} satellites)` : "Good";
  return (
    <section className={`strip ${staff ? "has-hw" : ""}`}>
      {sharing && <ShareDialog deviceId={d.id} deviceName={d.name} onClose={() => setSharing(false)} />}
      <div className="strip-col">
        <div className="strip-head">
          <strong title={d.name}>{d.name}</strong>
          <span className={`pill ${st}`} title={stoppedFor ? `Stopped for ${stoppedFor}` : undefined}>{STATUS_LABEL[st]}{stoppedFor && <span className="pill-time"> · {stoppedFor}</span>}</span>
          <span className="strip-actions">
            <button className="btn-ghost small" onClick={onHistory}>History</button>
            <button className="btn-ghost small share-btn" onClick={() => setSharing(!sharing)}>Share</button>
            {menu}
            <button className="x" onClick={onClose} aria-label="Close">×</button>
          </span>
        </div>
        {q !== "good" && <div className={`gps-warn q-${q}`} role="note">⚠ {QUALITY_NOTE[q]}</div>}
        {st === "late" && <div className="gps-warn q-late" role="note">⚠ No signal for {ago(s?.seen_at, now).replace(" ago", "")} — the tracker usually reports every {s?.report_s ? `${s.report_s} s` : "minute"}. Shown at its last known position.</div>}
        <dl>
          <dt>Position time</dt><dd className="mono">{fijiTime(s?.fix_at)}</dd>
          <dt>Last seen</dt><dd className="mono">{ago(s?.seen_at, now)}</dd>
          <dt>Coordinates</dt><dd className="mono">{s?.lat != null ? `${s.lat.toFixed(5)}, ${s.lng!.toFixed(5)}` : "—"}</dd>
          <dt>Speed</dt><dd className="mono">{st === "late" || st === "offline" ? `— (last ${kmh(s?.speed_kn)} km/h)` : `${kmh(s?.speed_kn)} km/h`}</dd>
          {(() => { const zs = zonesAt(zones, s?.lat, s?.lng); return zs.length ? <><dt>Zone</dt><dd title={zs.map((z) => z.name).join(" › ")}>{zoneStack(zs)}</dd></> : null; })()}
        </dl>
      </div>
      <div className="strip-col">
        <div className="strip-head"><strong>Device</strong></div>
        <dl>
          <dt>Status</dt><dd>{d.status === "active" ? (st === "offline" || st === "nodata" ? "Not reporting" : "Online") : d.status === "suspended" ? "Suspended" : d.status}</dd>
          <dt>GPS signal</dt><dd className={q !== "good" ? "warn-text" : ""}>{signal}</dd>
          <dt>Ignition</dt><dd>{s?.ignition == null ? "—" : s.ignition ? "On" : "Off"}</dd>
          <dt>Battery</dt><dd className="mono">{s?.battery != null ? `${s.battery}%` : "—"}</dd>
          <dt>Odometer</dt><dd className="mono">{a.totalDistance != null ? `${(Number(a.totalDistance) / 1000).toFixed(1)} km` : "—"}</dd>
        </dl>
      </div>
      {staff && (
        <div className="strip-col">
          <div className="strip-head"><strong>Hardware</strong><span className="sub">staff only</span></div>
          <dl>
            <dt>Model</dt><dd>{d.model ?? "—"}</dd>
            <dt>ID</dt><dd className="mono">{d.unique_id}</dd>
            <dt>Raw fix</dt><dd className="mono">{s?.raw_lat != null && q !== "good" ? `${s.raw_lat.toFixed(5)}, ${s.raw_lng!.toFixed(5)}` : s?.valid ? "Valid" : "—"}</dd>
          </dl>
        </div>
      )}
    </section>
  );
}

function escapeHtml(s: string) {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);
}
